import React, { useState } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Collapse,
    Box,
    Avatar,
    Typography,
    Divider,
    useTheme,
    useMediaQuery
} from "@mui/material";
import {
    Dashboard,
    People,
    School,
    TrendingUp,
    Settings,
    Logout,
    ExpandLess,
    ExpandMore,
    Menu as MenuIcon,
    Group,
    AutoStories,
    Build,
    Person
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { logoutUser } from "../firebase/auth";

export default function Sidebar() {
    const navigate = useNavigate();
    const { userProfile, user } = useAuth();
    const [openMenu, setOpenMenu] = useState({});
    const [mobileOpen, setMobileOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleLogout = async () => {
        try {
            await logoutUser();
            navigate("/login");
        } catch (error) {
            console.error("Logout failed:", error);
        }
    };

    const handleMenuToggle = (menu) => {
        setOpenMenu((prev) => ({
            ...prev,
            [menu]: !prev[menu]
        }));
    };

    const menuItems = [
        {
            label: "Dashboard",
            icon: <Dashboard />,
            path: userProfile?.role === "admin" ? "/admin" : userProfile?.role === "startup" ? "/startup" : "/student",
            roles: ["admin", "student", "startup", "faculty"]
        },
        {
            label: "Profile",
            icon: <Person />,
            path: userProfile?.role === "admin" ? "/admin" : userProfile?.role === "startup" ? "/startup/profile" : "/student/profile",
            roles: ["admin", "student", "startup"]
        },
        {
            label: "Users",
            icon: <People />,
            path: "/admin/users",
            roles: ["admin"]
        },
        {
            label: "Teams",
            icon: <Group />,
            path: userProfile?.role === "admin" ? "/admin/teams" : "/student/teams",
            roles: ["admin", "student"]
        },
        {
            label: "Learning",
            icon: <AutoStories />,
            submenu: [
                { label: "Browse Paths", path: "/student/learning/paths", roles: ["student"] },
                { label: "My Progress", path: "/student/learning/progress", roles: ["student"] },
                { label: "Manage Paths", path: "/admin/learning-paths", roles: ["admin"] },
                { label: "Create Path", path: "/admin/learning-paths/create", roles: ["admin"] }
            ],
            roles: ["student", "admin"]
        },
        {
            label: "Rankings",
            icon: <TrendingUp />,
            path: "/student/ranking",
            roles: ["student", "startup"]
        },
        {
            label: "Credits",
            icon: <School />,
            submenu: [
                { label: "My Credits", path: "/student/credits/history", roles: ["student"] },
                { label: "Assign Credits", path: "/admin/credits/assign", roles: ["admin"] }
            ],
            roles: ["student", "admin"]
        },
        {
            label: "Startups",
            icon: <Build />,
            submenu: [
                { label: "Browse Startups", path: "/student/startups", roles: ["student"] },
                { label: "Create Startup", path: "/student/startup/new", roles: ["student"] },
                { label: "My Startup", path: "/student/my-startup", roles: ["student"] },
                { label: "All Startups", path: "/admin/startups", roles: ["admin"] }
            ],
            roles: ["student", "admin", "startup"]
        },
        {
            label: "Form Builder",
            icon: <Build />,
            path: "/admin/form-builder",
            roles: ["admin"]
        },
        {
            label: "Scoreboard",
            icon: <TrendingUp />,
            path: "/admin/scoreboard",
            roles: ["admin"]
        },
        {
            label: "Settings",
            icon: <Settings />,
            path: "/settings",
            roles: ["admin", "student", "startup", "faculty"]
        }
    ];

    const visibleItems = menuItems.filter((item) =>
        item.roles?.includes(userProfile?.role)
    );

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const drawerContent = (
        <>
            <Box sx={{ p: 2, textAlign: "center", background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
                <Avatar
                    sx={{
                        width: 64,
                        height: 64,
                        mx: "auto",
                        mb: 1,
                        backgroundColor: "rgba(255,255,255,0.3)",
                        border: "3px solid white"
                    }}
                >
                    {userProfile?.firstName?.charAt(0) || "U"}
                </Avatar>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {userProfile?.firstName} {userProfile?.lastName}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    {userProfile?.role?.charAt(0).toUpperCase() + userProfile?.role?.slice(1) || "User"}
                </Typography>
            </Box>

            <Divider />

            <List sx={{ pt: 2 }}>
                {visibleItems.map((item) => (
                    <div key={item.label}>
                        {item.submenu ? (
                            <>
                                <ListItem
                                    button
                                    onClick={() => handleMenuToggle(item.label)}
                                    sx={{
                                        "&:hover": { backgroundColor: "action.hover" }
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                                        {item.icon}
                                    </ListItemIcon>
                                    <ListItemText primary={item.label} />
                                    {openMenu[item.label] ? <ExpandLess /> : <ExpandMore />}
                                </ListItem>
                                <Collapse in={openMenu[item.label]} timeout="auto" unmountOnExit>
                                    <List component="div" disablePadding>
                                        {item.submenu
                                            .filter((sub) => sub.roles?.includes(userProfile?.role))
                                            .map((sub) => (
                                                <ListItem
                                                    button
                                                    key={sub.label}
                                                    onClick={() => handleNavigate(sub.path)}
                                                    sx={{
                                                        pl: 4,
                                                        "&:hover": { backgroundColor: "action.hover" }
                                                    }}
                                                >
                                                    <ListItemText primary={sub.label} />
                                                </ListItem>
                                            ))}
                                    </List>
                                </Collapse>
                            </>
                        ) : (
                            <ListItem
                                button
                                onClick={() => handleNavigate(item.path)}
                                sx={{
                                    "&:hover": { backgroundColor: "action.hover" }
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40, color: "primary.main" }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText primary={item.label} />
                            </ListItem>
                        )}
                    </div>
                ))}
            </List>

            <Divider sx={{ my: 2 }} />

            <List>
                <ListItem
                    button
                    onClick={handleLogout}
                    sx={{
                        "&:hover": { backgroundColor: "error.lighter" },
                        color: "error.main"
                    }}
                >
                    <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}>
                        <Logout />
                    </ListItemIcon>
                    <ListItemText primary="Logout" />
                </ListItem>
            </List>
        </>
    );

    return (
        <>
            {isMobile && (
                <Box
                    sx={{
                        p: 1,
                        display: "flex",
                        justifyContent: "flex-start",
                        backgroundColor: "background.paper",
                        borderBottom: "1px solid",
                        borderColor: "divider"
                    }}
                >
                    <MenuIcon
                        onClick={() => setMobileOpen(!mobileOpen)}
                        sx={{ cursor: "pointer" }}
                    />
                </Box>
            )}

            <Drawer
                variant={isMobile ? "temporary" : "permanent"}
                open={isMobile ? mobileOpen : true}
                onClose={() => setMobileOpen(false)}
                sx={{
                    width: 280,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: 280,
                        boxSizing: "border-box",
                        top: isMobile ? 0 : 64,
                        height: isMobile ? "100vh" : "calc(100vh - 64px)"
                    }
                }}
            >
                {drawerContent}
            </Drawer>
        </>
    );
}
